import {controller, target} from '@github/catalyst'

interface SearchResult {
  id: string
  text: string
  description?: string
}

@controller
class AutocompleteInputElement extends HTMLElement {
  @target input: HTMLInputElement
  @target results: HTMLElement
  @target selectedItems: HTMLElement

  private debounceTimer: ReturnType<typeof setTimeout> | null = null
  private selectedIds: Set<string> = new Set()
  private abortController: AbortController | null = null
  private resultsEl!: HTMLElement
  private handleOutsideClick = (e: Event) => this.onOutsideClick(e)

  get src(): string {
    return this.getAttribute('data-src') || ''
  }

  get multiple(): boolean {
    return this.hasAttribute('data-multiple')
  }

  get fieldName(): string {
    const name = this.getAttribute('data-name') || ''
    return this.multiple ? `${name}[]` : name
  }

  connectedCallback() {
    this.input.addEventListener('input', () => this.onInput())
    this.input.addEventListener('keydown', (e) => this.onKeydown(e))
    document.addEventListener('click', this.handleOutsideClick)

    // Move results to body to escape overflow/containment clipping from parent containers
    this.resultsEl = this.results
    document.body.appendChild(this.resultsEl)

    // Collect any pre-selected IDs
    this.selectedItems.querySelectorAll<HTMLInputElement>('input[type="hidden"]').forEach((input) => {
      this.selectedIds.add(input.value)
    })

    // Collect excluded IDs (items already added)
    const excludeAttr = this.getAttribute('data-exclude')
    if (excludeAttr) {
      excludeAttr.split(',').forEach(id => {
        const trimmed = id.trim()
        if (trimmed) this.selectedIds.add(trimmed)
      })
    }
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.handleOutsideClick)
    this.resultsEl?.remove()
  }

  private onInput() {
    const query = this.input.value.trim()
    if (query.length < 1) {
      this.hideResults()
      return
    }

    if (this.debounceTimer) clearTimeout(this.debounceTimer)
    this.debounceTimer = setTimeout(() => this.fetchResults(query), 250)
  }

  private onKeydown(e: KeyboardEvent) {
    if (e.key === 'Escape') {
      this.hideResults()
      this.input.blur()
    }
  }

  private onOutsideClick(e: Event) {
    const target = e.target as Node
    if (!this.contains(target) && !this.resultsEl.contains(target)) {
      this.hideResults()
    }
  }

  private async fetchResults(query: string) {
    if (this.abortController) this.abortController.abort()
    this.abortController = new AbortController()

    const params = new URLSearchParams({q: query})
    for (const id of this.selectedIds) {
      params.append('exclude[]', id)
    }
    const url = `${this.src}?${params}`

    try {
      const response = await fetch(url, {
        headers: {'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest'},
        signal: this.abortController.signal,
      })

      if (!response.ok) return

      const data: SearchResult[] = await response.json()
      this.renderResults(data)
    } catch (e) {
      if (e instanceof DOMException && e.name === 'AbortError') return
    }
  }

  private renderResults(data: SearchResult[]) {
    if (data.length === 0) {
      this.resultsEl.innerHTML = `
        <div class="px-4 py-3 text-zinc-500 text-sm">No results found</div>
      `
      this.showResults()
      return
    }

    this.resultsEl.innerHTML = data.map((item) => `
      <button
        type="button"
        class="w-full text-left px-4 py-3 hover:bg-zinc-700/50 transition-colors cursor-pointer"
        data-id="${this.escapeHtml(item.id)}"
        data-text="${this.escapeHtml(item.text)}"
      >
        <div class="text-white text-sm">${this.escapeHtml(item.text)}</div>
        ${item.description ? `<div class="text-zinc-500 text-xs mt-0.5">${this.escapeHtml(item.description)}</div>` : ''}
      </button>
    `).join('')

    this.resultsEl.querySelectorAll('button').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault()
        const id = btn.getAttribute('data-id') || ''
        const text = btn.getAttribute('data-text') || ''
        this.selectItem(id, text)
      })
    })

    this.showResults()
  }

  private selectItem(id: string, text: string) {
    if (this.multiple) {
      this.addSelectedPill(id, text)
      this.input.value = ''
    } else {
      this.setSingleValue(id, text)
    }

    this.selectedIds.add(id)
    this.hideResults()
  }

  private setSingleValue(id: string, text: string) {
    this.input.value = text
    this.input.dataset.selectedId = id

    // Remove existing hidden input if any
    const existing = this.querySelector<HTMLInputElement>(`input[name="${this.fieldName}"]`)
    if (existing) existing.value = id
    else {
      const hidden = document.createElement('input')
      hidden.type = 'hidden'
      hidden.name = this.fieldName
      hidden.value = id
      this.appendChild(hidden)
    }
  }

  private addSelectedPill(id: string, text: string) {
    const pill = document.createElement('div')
    pill.className = 'inline-flex items-center gap-1.5 bg-zinc-800 text-zinc-300 text-sm px-3 py-1.5 rounded-lg'
    pill.innerHTML = `
      <span>${this.escapeHtml(text)}</span>
      <button type="button" class="text-zinc-500 hover:text-zinc-300 transition-colors cursor-pointer" data-remove-id="${this.escapeHtml(id)}">
        <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
        </svg>
      </button>
      <input type="hidden" name="${this.fieldName}" value="${this.escapeHtml(id)}">
    `

    const removeBtn = pill.querySelector('[data-remove-id]')!
    removeBtn.addEventListener('click', () => {
      this.selectedIds.delete(id)
      pill.remove()
    })

    this.selectedItems.appendChild(pill)
  }

  private positionResults() {
    const rect = this.input.getBoundingClientRect()
    this.resultsEl.style.top = `${rect.bottom + 4}px`
    this.resultsEl.style.left = `${rect.left}px`
    this.resultsEl.style.width = `${rect.width}px`
  }

  private showResults() {
    this.positionResults()
    this.resultsEl.hidden = false
  }

  private hideResults() {
    this.resultsEl.hidden = true
  }

  private escapeHtml(text: string): string {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }
}
